import { useMemo } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle } from "lucide-react";
import useSWR from "swr";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { nextgenProjectPath, nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import {
  Project,
  ProjectConversation,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectPath,
} from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { ProjectSidebar } from "./ProjectSidebar";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  sessions: ProjectConversation[] | undefined;
  attachedAgents: Agent[] | undefined;
  attachedDatasets: Dataset[] | undefined;
}

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including featured_conversations) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally featured_conversations would be included in that endpoint to avoid N requests.
  const { data: agentsWithFeatured } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );

  const { data: attachedDatasetIds } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasets } = useFetcher<Dataset[]>(CATALOG_DATASETS_URL);

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const attachedDatasets = useMemo<Dataset[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) {
      return undefined;
    }
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds.map((id) => byId.get(id)).filter((d): d is Dataset => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const conversationId = location.pathname.match(/\/c\/([^/]+)/)?.[1];
  const featuredToken = location.pathname.match(/\/shared\/([^/]+)/)?.[1];
  const isSettings =
    location.pathname.endsWith("/settings") || location.pathname.endsWith("/settings-v2");
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;
  const currentFeaturedLabel = featuredToken
    ? agentsWithFeatured
        ?.flatMap((a) => a.featured_conversations ?? [])
        .find((fc) => fc.url.endsWith(featuredToken))?.label
    : undefined;

  const breadcrumbs = useMemo(() => {
    const crumbs: { label: string; href?: string }[] = [
      { label: "All Projects", href: nextgenProjectsListPath() },
      {
        label: project?.name ?? "Loading…",
        href:
          (conversationId || featuredToken || isSettings) && projectId
            ? nextgenProjectPath(projectId)
            : undefined,
      },
    ];

    if (conversationId) {
      crumbs.push({ label: currentSession?.display_name ?? "Loading…" });
    } else if (featuredToken) {
      crumbs.push({ label: currentFeaturedLabel ?? "Featured Session" });
    } else if (isSettings) {
      crumbs.push({ label: "Settings" });
    }

    return crumbs;
  }, [
    project?.name,
    projectId,
    conversationId,
    featuredToken,
    isSettings,
    currentSession?.display_name,
    currentFeaturedLabel,
  ]);

  useNextGenHeader({ breadcrumbs });

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load project</p>
      </div>
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    sessions,
    attachedAgents,
    attachedDatasets,
  };

  return (
    <div className="flex h-full overflow-hidden">
      <ProjectSidebar
        projectId={projectId}
        projectName={project.name}
        sessions={sessions}
        attachedAgents={agentsWithFeatured}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};
